import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import RichTextEditor from '@/components/RichTextEditor';
import ImageUpload from '@/components/ImageUpload';
import SchedulingControls from '@/components/SchedulingControls';
import SchedulePickerDialog, { formatLisbon } from '@/components/SchedulePickerDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  BookOpen, 
  Plus, 
  Edit, 
  Trash2, 
  Euro, 
  Users, 
  TrendingUp,
  Loader2,
  X,
  Image,
  FileText,
  Upload,
  Download
} from 'lucide-react';
import { extractErrorMessage } from '@/lib/api-errors';

function stripHtml(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

const TYPE_COPY = {
  story: {
    pageTitle: 'Gestão de Séries',
    pageSubtitle: 'Cria e gerencie séries premium com episódios.',
    newButton: 'Nova Série',
    newDialogTitle: 'Nova Série',
    editDialogTitle: 'Editar Série',
    fieldLabel: 'Título da Série',
    descLabel: 'Descrição da Série',
    coverLabel: 'Capa da Série',
    emptyText: 'Nenhuma série criada',
    emptyHint: 'Cria a sua primeira série!',
    addEpisodeLabel: '+ Episódio',
    purchasesLabel: 'Compras',
  },
  guide: {
    pageTitle: 'Gestão Conhece-te',
    pageSubtitle: 'Cria guias práticos com capítulos de leitura e PDF descarregável.',
    newButton: 'Novo Guia',
    newDialogTitle: 'Novo Guia',
    editDialogTitle: 'Editar Guia',
    fieldLabel: 'Título do Guia',
    descLabel: 'Descrição do Guia',
    coverLabel: 'Capa do Guia',
    emptyText: 'Nenhum guia criado',
    emptyHint: 'Cria o seu primeiro guia Conhece-te!',
    addEpisodeLabel: '+ Capítulo',
    purchasesLabel: 'Compras',
  },
};

export default function AdminSeries({ seriesType = 'story' }) {
  const copy = TYPE_COPY[seriesType] || TYPE_COPY.story;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [series, setSeries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfUploadingId, setPdfUploadingId] = useState(null);
  
  // Dialog states
  const [seriesDialogOpen, setSeriesDialogOpen] = useState(false);
  const [episodeDialogOpen, setEpisodeDialogOpen] = useState(false);
  const [editEpisodeDialogOpen, setEditEpisodeDialogOpen] = useState(false);
  const [editingSeries, setEditingSeries] = useState(null);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [editingEpisode, setEditingEpisode] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form states
  const [seriesForm, setSeriesForm] = useState({
    title: '',
    description: '',
    cover_image: '',
    price: 4.99
  });
  const [episodeForm, setEpisodeForm] = useState({
    title: '',
    content: '',
    synopsis: '',
    cover_image: ''
  });
  const [editEpisodeForm, setEditEpisodeForm] = useState({
    title: '',
    content: '',
    synopsis: '',
    cover_image: ''
  });

  // Scheduling for the episode dialog (create OR edit)
  const [episodeScheduledAt, setEpisodeScheduledAt] = useState(null);
  const [episodeSchedulePickerOpen, setEpisodeSchedulePickerOpen] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, seriesType]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [seriesRes, statsRes] = await Promise.all([
        api.get('/admin/series', { params: { series_type: seriesType } }),
        api.get('/admin/series/stats')
      ]);
      setSeries(seriesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSeriesPdf = async (seriesId, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Apenas ficheiros PDF são aceites');
      return;
    }
    setPdfUploadingId(seriesId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/admin/series/${seriesId}/upload-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('PDF carregado com sucesso');
      loadData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao carregar PDF'));
    } finally {
      setPdfUploadingId(null);
    }
  };

  const handleRemoveSeriesPdf = async (seriesId) => {
    if (!window.confirm('Remover o PDF associado a esta série?')) return;
    try {
      await api.delete(`/admin/series/${seriesId}/pdf`);
      toast.success('PDF removido');
      loadData();
    } catch (error) {
      toast.error('Erro ao remover PDF');
    }
  };

  const openCreateDialog = () => {
    setEditingSeries(null);
    setSeriesForm({ title: '', description: '', cover_image: '', price: 4.99 });
    setSeriesDialogOpen(true);
  };

  const openEditDialog = (s) => {
    setEditingSeries(s);
    setSeriesForm({
      title: s.title,
      description: s.description,
      cover_image: s.cover_image || '',
      price: s.price
    });
    setSeriesDialogOpen(true);
  };

  const openEpisodeDialog = (s) => {
    setSelectedSeries(s);
    setEpisodeForm({ title: '', content: '', synopsis: '', cover_image: '' });
    setEpisodeScheduledAt(null);
    setEpisodeDialogOpen(true);
  };

  const openEditEpisodeDialog = async (s, ep) => {
    setSelectedSeries(s);
    try {
      const res = await api.get(`/admin/series/${s.id}/episodes/${ep.story_id}`);
      setEditingEpisode({ ...ep, ...res.data });
      setEditEpisodeForm({
        title: res.data.title || '',
        content: res.data.content || '',
        synopsis: res.data.synopsis || '',
        cover_image: res.data.cover_image || ''
      });
      setEpisodeScheduledAt(res.data.scheduled_at || null);
      setEditEpisodeDialogOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar episódio');
    }
  };

  const handleSeriesSubmit = async (e) => {
    e.preventDefault();
    if (!seriesForm.title || !seriesForm.description) {
      toast.error('Preenche todos os campos obrigatórios');
      return;
    }

    setSaving(true);
    try {
      if (editingSeries) {
        await api.put(`/admin/series/${editingSeries.id}`, seriesForm);
        toast.success(seriesType === 'guide' ? 'Guia atualizado' : 'Série atualizada');
      } else {
        await api.post('/admin/series', { ...seriesForm, series_type: seriesType });
        toast.success(seriesType === 'guide' ? 'Guia criado' : 'Série criada');
      }
      setSeriesDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleAddEpisode = async () => {
    if (!episodeForm.title.trim()) {
      toast.error('Título do episódio é obrigatório');
      return;
    }
    if (!stripHtml(episodeForm.content).trim()) {
      toast.error('Conteúdo do episódio é obrigatório');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: episodeForm.title,
        content: episodeForm.content,
        synopsis: episodeForm.synopsis,
        cover_image: episodeForm.cover_image
      };
      if (episodeScheduledAt) payload.scheduled_at = episodeScheduledAt;
      await api.post(`/admin/series/${selectedSeries.id}/episodes/create`, payload);
      toast.success(episodeScheduledAt ? `Episódio agendado para ${formatLisbon(episodeScheduledAt)}` : 'Episódio criado com sucesso!');
      setEpisodeDialogOpen(false);
      setEpisodeForm({ title: '', content: '', synopsis: '', cover_image: '' });
      setEpisodeScheduledAt(null);
      loadData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao criar episódio'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateEpisode = async () => {
    if (!editEpisodeForm.title.trim()) {
      toast.error('Título do episódio é obrigatório');
      return;
    }
    if (!stripHtml(editEpisodeForm.content).trim()) {
      toast.error('Conteúdo do episódio é obrigatório');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...editEpisodeForm };
      if (episodeScheduledAt) payload.scheduled_at = episodeScheduledAt;
      await api.put(
        `/admin/series/${selectedSeries.id}/episodes/${editingEpisode.story_id}`,
        payload
      );
      toast.success(episodeScheduledAt ? `Agendado para ${formatLisbon(episodeScheduledAt)}` : 'Episódio atualizado');
      setEditEpisodeDialogOpen(false);
      setEditingEpisode(null);
      setEpisodeScheduledAt(null);
      loadData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao atualizar episódio'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveEpisode = async (seriesId, storyId) => {
    if (!confirm('Remover este episódio da série?')) return;
    
    try {
      await api.delete(`/admin/series/${seriesId}/episodes/${storyId}`);
      toast.success('Episódio removido');
      loadData();
    } catch (error) {
      toast.error('Erro ao remover episódio');
    }
  };

  const handleDeleteSeries = async (seriesId) => {
    if (!confirm('Tem a certeza que deseja eliminar esta série?')) return;
    
    try {
      await api.delete(`/admin/series/${seriesId}`);
      toast.success('Série eliminada');
      loadData();
    } catch (error) {
      toast.error('Erro ao eliminar série');
    }
  };

  const handleToggleActive = async (s) => {
    try {
      await api.put(`/admin/series/${s.id}`, { is_active: !s.is_active });
      toast.success(s.is_active ? 'Série desativada' : 'Série ativada');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar série');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ruby" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 pt-24 pb-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-ruby/10 flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-ruby" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{copy.pageTitle}</h1>
              <p className="text-muted-foreground">{copy.pageSubtitle}</p>
            </div>
          </div>
          <Button onClick={openCreateDialog} className="bg-ruby hover:bg-ruby-light" data-testid="admin-series-new">
            <Plus className="w-4 h-4 mr-2" />
            {copy.newButton}
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-surface border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Séries</p>
                    <p className="text-2xl font-bold text-foreground">{stats.total_series}</p>
                  </div>
                  <BookOpen className="w-8 h-8 text-ruby" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-surface border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Compras</p>
                    <p className="text-2xl font-bold text-foreground">{stats.total_purchases}</p>
                  </div>
                  <Users className="w-8 h-8 text-blue-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-surface border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Receita Total</p>
                    <p className="text-2xl font-bold text-green-400">€{stats.total_revenue}</p>
                  </div>
                  <Euro className="w-8 h-8 text-green-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-surface border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Média/Série</p>
                    <p className="text-2xl font-bold text-gold">
                      €{stats.total_series > 0 ? (stats.total_revenue / stats.total_series).toFixed(2) : '0.00'}
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-gold" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Series List */}
        <Card className="bg-surface border-border">
          <CardHeader>
            <CardTitle className="text-foreground">Todas as Séries</CardTitle>
          </CardHeader>
          <CardContent>
            {series.length === 0 ? (
              <div className="text-center py-12">
                <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{copy.emptyText}</p>
                <p className="text-xs text-muted-foreground mt-1">{copy.emptyHint}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {series.map((s) => (
                  <div key={s.id} className="p-4 bg-white/5 rounded-lg">
                    <div className="flex items-start gap-4">
                      {/* Cover */}
                      <div className="w-24 h-32 rounded overflow-hidden shrink-0 bg-white/5">
                        {s.cover_image ? (
                          <img src={s.cover_image} alt={s.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="w-8 h-8 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-foreground">{s.title}</h3>
                          <Badge className={s.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                            {s.is_active ? 'Ativa' : 'Inativa'}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{s.description}</p>
                        
                        <div className="flex flex-wrap gap-4 text-sm">
                          <span className="text-ruby font-semibold">€{s.price.toFixed(2)}</span>
                          <span className="text-muted-foreground">
                            {s.episode_count} {seriesType === 'guide' ? 'capítulos' : 'episódios'}
                          </span>
                          <span className="text-muted-foreground">{s.purchase_count} compras</span>
                          <span className="text-green-400">€{s.total_revenue?.toFixed(2)} receita</span>
                          {s.pdf_url && (
                            <span className="text-gold inline-flex items-center gap-1">
                              <FileText className="w-3.5 h-3.5" />
                              PDF anexado
                            </span>
                          )}
                        </div>
                        
                        {/* Episodes Preview */}
                        {s.episodes && s.episodes.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-1">
                            {s.episodes.slice(0, 5).map((ep, idx) => (
                              <Badge key={idx} variant="outline" className="border-border text-xs">
                                Ep. {ep.episode_number}
                              </Badge>
                            ))}
                            {s.episodes.length > 5 && (
                              <Badge variant="outline" className="border-border text-xs">
                                +{s.episodes.length - 5}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <div className="flex flex-col gap-2 shrink-0">
                        <Button size="sm" variant="outline" onClick={() => openEpisodeDialog(s)} className="border-ruby text-ruby hover:bg-ruby/10">
                          <Plus className="w-4 h-4 mr-1" />
                          {seriesType === 'guide' ? 'Capítulo' : 'Episódio'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openEditDialog(s)} className="border-border">
                          <Edit className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                        {/* PDF upload (single file per series) */}
                        <label className="inline-flex">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-gold/40 text-gold hover:bg-gold/10 w-full"
                            asChild
                          >
                            <span className="cursor-pointer flex items-center justify-center">
                              {pdfUploadingId === s.id ? (
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              ) : s.pdf_url ? (
                                <Edit className="w-4 h-4 mr-1" />
                              ) : (
                                <Upload className="w-4 h-4 mr-1" />
                              )}
                              {s.pdf_url ? 'Substituir PDF' : 'Carregar PDF'}
                            </span>
                          </Button>
                          <input
                            type="file"
                            accept="application/pdf"
                            className="hidden"
                            data-testid={`pdf-input-${s.id}`}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) handleUploadSeriesPdf(s.id, f);
                              e.target.value = '';
                            }}
                          />
                        </label>
                        {s.pdf_url && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveSeriesPdf(s.id)}
                            className="text-red-400 hover:bg-red-500/10"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Remover PDF
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => handleToggleActive(s)} className="border-border">
                          {s.is_active ? 'Desativar' : 'Ativar'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDeleteSeries(s.id)} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Episodes Management */}
                    {s.episodes && s.episodes.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <p className="text-sm font-medium text-muted-foreground mb-2">
                          <FileText className="w-4 h-4 inline mr-1" />
                          Episódios:
                        </p>
                        <div className="space-y-1">
                          {s.episodes.sort((a, b) => a.episode_number - b.episode_number).map((ep) => (
                            <div key={ep.story_id} className="flex items-center gap-2 text-sm" data-testid={`episode-row-${ep.story_id}`}>
                              <Badge variant="outline" className="border-ruby/30 text-ruby w-16 justify-center shrink-0">
                                Ep. {ep.episode_number}
                              </Badge>
                              {ep.cover_image ? (
                                <img
                                  src={ep.cover_image}
                                  alt=""
                                  className="w-10 h-10 rounded object-cover shrink-0 border border-border"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded bg-background border border-dashed border-border shrink-0 flex items-center justify-center">
                                  <Image className="w-4 h-4 text-muted-foreground" />
                                </div>
                              )}
                              <span className="text-muted-foreground flex-1 truncate">
                                {ep.title || `Episódio ${ep.episode_number}`}
                              </span>
                              <SchedulingControls
                                resource="stories"
                                item={{ id: ep.story_id, title: ep.title, status: ep.status, scheduled_at: ep.scheduled_at }}
                                publishedFlag={(it) => it.status === 'published'}
                                onChanged={loadData}
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditEpisodeDialog(s, ep)}
                                className="h-7 w-7 p-0 text-gold hover:text-gold hover:bg-gold/10"
                                data-testid={`edit-episode-${ep.story_id}`}
                                title="Editar episódio"
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRemoveEpisode(s.id, ep.story_id)}
                                className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
                                title="Remover episódio"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Series Dialog */}
        <Dialog open={seriesDialogOpen} onOpenChange={setSeriesDialogOpen}>
          <DialogContent className="bg-surface border-border max-w-lg z-50">
            <DialogHeader>
              <DialogTitle className="text-foreground">
                {editingSeries ? copy.editDialogTitle : copy.newDialogTitle}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSeriesSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Título *</Label>
                <Input
                  value={seriesForm.title}
                  onChange={(e) => setSeriesForm({ ...seriesForm, title: e.target.value })}
                  placeholder={copy.fieldLabel}
                  className="bg-background border-border"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Descrição *</Label>
                <Textarea
                  value={seriesForm.description}
                  onChange={(e) => setSeriesForm({ ...seriesForm, description: e.target.value })}
                  placeholder={copy.descLabel}
                  className="bg-background border-border min-h-[100px]"
                  required
                />
              </div>
              <ImageUpload
                value={seriesForm.cover_image}
                onChange={(url) => setSeriesForm({ ...seriesForm, cover_image: url })}
                label={copy.coverLabel}
                aspectRatio="aspect-[2/3]"
                recommendedSize="900×1200 · 3:4 · até 250 KB"
                hint={seriesType === 'guide'
                  ? 'Capa do guia Conhece-te — tipo capa de livro.'
                  : 'Capa da série — tipo pôster vertical. Evita texto pequeno.'}
              />
              <div className="space-y-2">
                <Label>Preço (EUR) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.50"
                  value={seriesForm.price}
                  onChange={(e) => setSeriesForm({ ...seriesForm, price: parseFloat(e.target.value) || 0 })}
                  className="bg-background border-border"
                  required
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setSeriesDialogOpen(false)} className="border-border">
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} className="bg-ruby hover:bg-ruby-light">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingSeries ? 'Guardar' : copy.newButton}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Create Episode Dialog */}
        <Dialog open={episodeDialogOpen} onOpenChange={setEpisodeDialogOpen}>
          <DialogContent className="bg-surface border-border max-w-3xl max-h-[90vh] overflow-y-auto z-50">
            <DialogHeader>
              <DialogTitle className="text-foreground">
                Novo Episódio — {selectedSeries?.title}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Título do Episódio *</Label>
                <Input
                  value={episodeForm.title}
                  onChange={(e) => setEpisodeForm({ ...episodeForm, title: e.target.value })}
                  placeholder="Ex: Capítulo 1 - O Encontro"
                  className="bg-background border-border"
                />
              </div>
              <div className="space-y-2">
                <Label>Sinopse (opcional)</Label>
                <Textarea
                  value={episodeForm.synopsis}
                  onChange={(e) => setEpisodeForm({ ...episodeForm, synopsis: e.target.value })}
                  placeholder="Breve descrição do episódio..."
                  className="bg-background border-border"
                  maxLength={300}
                />
              </div>
              <ImageUpload
                value={episodeForm.cover_image}
                onChange={(url) => setEpisodeForm({ ...episodeForm, cover_image: url })}
                label="Capa do Episódio (opcional)"
                aspectRatio="aspect-video"
                recommendedSize="1600×900 · 16:9 · até 250 KB"
                hint="Horizontal cinemático. Se deixares vazio, usa a capa da série."
              />
              <div className="space-y-2">
                <Label>Conteúdo *</Label>
                <RichTextEditor
                  content={episodeForm.content}
                  onChange={(html) => setEpisodeForm({ ...episodeForm, content: html })}
                  placeholder="Escreve o episódio aqui..."
                  minHeight="300px"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Este episódio será exclusivo da série. Apenas utilizadores Premium terão acesso.
              </p>
            </div>
            <DialogFooter>
              {episodeScheduledAt && (
                <div className="text-xs text-amber-300 mr-auto bg-amber-500/10 border border-amber-500/40 rounded px-3 py-1.5" data-testid="episode-scheduled-preview">
                  Agendado · {formatLisbon(episodeScheduledAt)}
                  <button type="button" className="ml-2 underline hover:text-amber-200" onClick={() => setEpisodeScheduledAt(null)}>
                    limpar
                  </button>
                </div>
              )}
              <Button variant="outline" onClick={() => setEpisodeDialogOpen(false)} className="border-border">
                Cancelar
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEpisodeSchedulePickerOpen(true)}
                className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
                data-testid="episode-open-schedule"
              >
                {episodeScheduledAt ? 'Alterar agendamento' : 'Agendar'}
              </Button>
              <Button onClick={handleAddEpisode} disabled={saving} className="bg-ruby hover:bg-ruby-light">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                {episodeScheduledAt ? 'Agendar Episódio' : 'Criar Episódio'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Edit Episode Dialog */}
        <Dialog open={editEpisodeDialogOpen} onOpenChange={setEditEpisodeDialogOpen}>
          <DialogContent
            className="bg-surface border-border max-w-3xl max-h-[90vh] overflow-y-auto z-50"
            data-testid="edit-episode-dialog"
          >
            <DialogHeader>
              <DialogTitle className="text-foreground">
                Editar Episódio {editingEpisode ? `${editingEpisode.episode_number}` : ''} — {selectedSeries?.title}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Título do Episódio *</Label>
                <Input
                  value={editEpisodeForm.title}
                  onChange={(e) => setEditEpisodeForm({ ...editEpisodeForm, title: e.target.value })}
                  placeholder="Ex: Capítulo 1 - O Encontro"
                  className="bg-background border-border"
                  data-testid="edit-episode-title"
                />
              </div>
              <div className="space-y-2">
                <Label>Sinopse (opcional)</Label>
                <Textarea
                  value={editEpisodeForm.synopsis}
                  onChange={(e) => setEditEpisodeForm({ ...editEpisodeForm, synopsis: e.target.value })}
                  placeholder="Breve descrição do episódio..."
                  className="bg-background border-border"
                  maxLength={300}
                  data-testid="edit-episode-synopsis"
                />
              </div>
              <ImageUpload
                value={editEpisodeForm.cover_image}
                onChange={(url) => setEditEpisodeForm({ ...editEpisodeForm, cover_image: url })}
                label="Capa do Episódio"
                aspectRatio="aspect-video"
                recommendedSize="1600×900 · 16:9 · até 250 KB"
                hint="Horizontal cinemático. Se deixares vazio, usa a capa da série."
              />
              <div className="space-y-2">
                <Label>Conteúdo *</Label>
                <RichTextEditor
                  content={editEpisodeForm.content}
                  onChange={(html) => setEditEpisodeForm({ ...editEpisodeForm, content: html })}
                  placeholder="Escreve o episódio aqui..."
                  minHeight="300px"
                />
              </div>
            </div>
            <DialogFooter>
              {episodeScheduledAt && (
                <div className="text-xs text-amber-300 mr-auto bg-amber-500/10 border border-amber-500/40 rounded px-3 py-1.5">
                  Agendado · {formatLisbon(episodeScheduledAt)}
                  <button type="button" className="ml-2 underline hover:text-amber-200" onClick={() => setEpisodeScheduledAt(null)}>
                    limpar
                  </button>
                </div>
              )}
              <Button variant="outline" onClick={() => setEditEpisodeDialogOpen(false)} className="border-border">
                Cancelar
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEpisodeSchedulePickerOpen(true)}
                className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
              >
                {episodeScheduledAt ? 'Alterar agendamento' : 'Agendar'}
              </Button>
              <Button
                onClick={handleUpdateEpisode}
                disabled={saving}
                className="bg-ruby hover:bg-ruby-light"
                data-testid="edit-episode-save"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Edit className="w-4 h-4 mr-2" />}
                {episodeScheduledAt ? 'Guardar Agendamento' : 'Guardar Alterações'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <SchedulePickerDialog
          open={episodeSchedulePickerOpen}
          onOpenChange={setEpisodeSchedulePickerOpen}
          description={editingEpisode?.title || episodeForm.title || 'Episódio'}
          initialIso={episodeScheduledAt}
          confirmLabel="Confirmar agendamento"
          onConfirm={async (iso) => {
            setEpisodeScheduledAt(iso);
            setEpisodeSchedulePickerOpen(false);
          }}
        />
      </main>
    </div>
  );
}
